import { utils, sitecore, RestApiDatatable, event } from '../../Foundation';
import { Select } from '../select';
import { DatatableTable } from '../../Foundation/datatable-table';
import { DatatableHeader } from '../../Foundation/datatable-header';
import { DatatableBody } from '../../Foundation/datatable-body';
import { DatatableFooter } from '../../Foundation/datatable-footer';

export class DataTableSpot {
  constructor(element) {
    this._refs = {
      el: element,
      filtersElem: element.querySelector('.filters'),
      clearFilter: element.querySelector('.clear-filters-field'),
      table: RestApiDatatable.checkTableElement(element),
      currentPage: 1,
      initSort: {
        row: 'A',
        order: 'asc',
      },
      state: {
        data: [],
        totalItems: 0,
      },
      settings: {
        maxPages: 5,
        pageSize: {
          current: 10,
          values: [10, 25, 50],
        },
        translations: {
          footerInfo:
            element.getAttribute('data-pagingInfo-labels') ||
            'Showing {startIndex} to {endIndex} of {total} results',
          noItems:
            element.getAttribute('data-api-noresults') ||
            'No results were found with current filters, please try again',
          clearFilters: element.getAttribute('data-clear-labels') || 'Clear all filters',
        },
      },
      queryString: {},
      eventList: {
        updateTable: (elem) => {
          this._refs.table = elem;
        },
        customHeader: (row, key, item) => {
          this._customTableHeader(row, key, item);
        },
        customBody: (row, key, item) => {
          this._customTableBody(row, key, item);
        },
        onArrowClick: (e) => {
          this._onPaginationArrowClick(e);
        },
        onPageItemClick: (e) => {
          this._onPaginationItemClick(e);
        },
        onChangePageSize: (e) => {
          this._onPagesizeChange(e);
        },
      },
    };

    if (!sitecore.isInPageEditor) {
      this._init();
    }
  }

  async _init() {
    // Fetch data
    await this._fetchData();
    // Create Filtering
    this._createFilters();
    // Create Datatable table
    await this._createTable();
    // Create Datatable header
    await this._createTableHeader();
    // Create Datatable body
    await this._createTableBody();
    // Create Datatable footer
    await this._createTableFooter();
    // Sort contents
    this._sortContentEvent();
  }

  /**
   * Fetch and update data
   **/
  _fetchData() {
    // Use live data
    const liveData = JSON.parse(this._refs.el.getAttribute('data-json'));
    this._updateState(liveData);
  }

  async _updateState(contents) {
    this._refs.state.data = contents.items;
    this._refs.state.backupData = contents.items;
    this._refs.state.totalItems = contents.items.length;
    this._refs.state.header = contents.headers;
    this._updateQueryString();
  }

  _updateQueryString() {
    for (const headObj of this._refs.state.header) {
      this._refs.queryString[headObj.key] = '';
    }
  }

  /**
   * Create datatable table
   **/
  _createTable() {
    const tableSettings = { ...this._refs };
    new DatatableTable(tableSettings);
  }

  /**
   * Create datatable header
   **/
  _createTableHeader() {
    const tableHeaderSettings = { ...this._refs };
    tableHeaderSettings.customLabels = this._refs.state.header;
    new DatatableHeader(tableHeaderSettings);
  }

  _customTableHeader(trElement, label, i) {
    const sortable = i === 0 ? `is-sortable is-${this._refs.initSort.order}` : 'is-sortable';
    const textLabel = utils.createElement(
      'th',
      {
        class: `datatable__cell font-bold`,
      },
      trElement,
    );
    utils.createElement(
      'span',
      {
        class: sortable,
        value: label.key,
        innerText: label.value,
      },
      textLabel,
    );
  }

  /**
   * Create datatable body
   **/
  _createTableBody() {
    // Filter contents
    this._filterContents();
    // Init sorting
    this._initSorting();
    // create content view
    const tableBodySettings = { ...this._refs };
    new DatatableBody(tableBodySettings);
  }

  _customTableBody(trElement, key, item) {
    // Append filterable content
    const iconObj = this._fetchIcon(item[key]);
    const linkObj = this._fetchLink(item[key]);
    if (iconObj.isIcon) {
      // Fetch icon and color
      const color = iconObj.color ? `color:#${iconObj.color};` : '';
      const icon = iconObj.icon;
      // Create icon container
      const content = utils.createElement(
        'td',
        {
          class: 'datatable__cell',
          // 'class': 'datatable__cell d-flex justify-content-center' // center icons
        },
        trElement,
      );
      utils.createElement(
        'i',
        {
          class: `eva eva-${icon}`,
          style: `font-size:24px;${color}`,
          'aria-hidden': 'true',
        },
        content,
      );
    } else if (linkObj.isLink) {
      // Get link type
      const linkType = linkObj.type.toLowerCase();
      const linkTarget = linkType === 'web' ? '_blank' : '';
      let type = linkObj.url.includes('http') ? '' : 'https://'; // Add http if not inc. in link
      if (linkType === 'phone') {
        type = 'tel:';
      } else if (linkType === 'email') {
        type = 'mailto:';
      }
      // Table data
      const content = utils.createElement(
        'td',
        {
          class: 'datatable__cell',
        },
        trElement,
      );
      // data link
      utils.createElement(
        'a',
        {
          href: `${type}${linkObj.url}`,
          innerText: linkObj.url,
          target: linkTarget,
        },
        content,
      );
    } else {
      // Create text container
      utils.createElement(
        'td',
        {
          class: 'datatable__cell',
          innerText: item[key],
        },
        trElement,
      );
    }
  }

  /**
   * Create datatable footer
   **/
  _createTableFooter() {
    const tableFooterSettings = { ...this._refs };
    new DatatableFooter(tableFooterSettings);
  }

  /**
   * Filters
   */
  _createFilters() {
    const headerArray = this._refs.state.header.filter((item) => item.filterable); // Only create filters if filterable is true
    for (const head of headerArray) {
      const headKey = head.key;
      const headLabel = head.value;
      let labelList = this._refs.state.data
        .map((field) => this._fetchFilterValue(field[headKey])) // return as Array
        .filter((item) => item.length > 0); // Filter empty string
      labelList = [...new Set(labelList)].sort(); // sort out duplicates
      if (labelList.length > 0) {
        labelList = [headLabel, ...labelList];
      }
      this._createDropDown([headKey, headLabel], labelList);
    }
  }

  _createDropDown(label, filterOptions) {
    const oCol = utils.createElement('div', {
        class: 'col-xs-12 col-sm-4 col-md-3',
      }),
      oHtml = utils.createElement(
        'div',
        {
          class: 'form-select datatable-select',
          'data-select': JSON.stringify({ placeholder: label[1] }),
          'data-key': label[0],
        },
        oCol,
      ),
      oSelect = utils.createElement(
        'select',
        {
          name: label[1],
          innerHTML: `${filterOptions
            .map((item) => {
              return `<option value="${item}" name="${item}">${item}</option>`;
            })
            .join('')}`,
        },
        oHtml,
      );

    new Select(oHtml);
    this._refs.filtersElem.append(oCol);

    oSelect.addEventListener('change', (e) => {
      // Update current page to first page
      this._refs.currentPage = 1;
      // Update filter query
      this._setFilters(label[0], e.target.value);
      // Update view
      this._updateViewItems();
    });
  }

  _setFilters(key, value) {
    // Return data value in filter format
    let arrValues = this._refs.state.backupData
      .map((obj) => obj[key]) // return array of key value
      .map((val) => this._fetchFilterValue(val));
    // Get filter value
    let filterValue = this._fetchFilterValue(value);
    // Check if content data value matches filter value
    let isFilterVal = arrValues.includes(filterValue);
    this._refs.queryString[key] = isFilterVal ? filterValue : '';
  }

  _filterContents() {
    this._refs.state.data = this._refs.state.backupData.filter((item) => {
      // Sort out empty filters
      const listOfFilters = Object.entries(this._refs.queryString).filter((arr) => arr[1]);
      if (listOfFilters.length > 0) {
        let match = true;
        // Filter all contents
        for (const [index, filter] of listOfFilters.entries()) {
          const filterValue = this._fetchFilterValue(item[filter[0]]);
          // If content does not match on one or multiple filters
          if (filterValue !== filter[1]) {
            match = false;
          }

          // If content match all filters
          if (index == listOfFilters.length - 1 && match) {
            return item;
          }
        }
      } else {
        return item;
      }
    });

    // Update total items in state
    this._refs.state.totalItems = this._refs.state.data.length;

    // Get filter columns from state
    const activeFilters = Object.keys(this._refs.queryString).map((key) => {
      return this._refs.state.data.map((item) => {
        const obj = {};
        obj[key] = this._fetchFilterValue(item[key]);
        return obj;
      });
    });

    // enable / disable options for each filters
    for (const filter of activeFilters) {
      this._disabledFilter(filter);
    }

    // Add / remove clear filter button
    this._clearFiltersButton();
  }

  // Disable filter with no result
  _disabledFilter(filter) {
    const key = Object.keys(filter[0]);
    const allValues = filter.map((item) => Object.values(item)[0]);
    const listValues = [...new Set(allValues)];
    const filterElem = this._refs.filtersElem.querySelector(`[data-key="${key}"]`);
    if (filterElem) {
      // Get filter options available
      const options = Array.from(filterElem.querySelectorAll('option'));
      const listItems = Array.from(filterElem.querySelectorAll('.form-select__list-item'));
      for (const [index, option] of options.entries()) {
        // Add class disabled if filter option does not exist in state
        if (index > 0 && !listValues.includes(option.value)) {
          // select option add disabled attr
          // li remove class is-disabled
          option.setAttribute('disabled', '');
          listItems[index].classList.add('is-disabled');
        } else {
          // select option remove disabled attr
          // li remove class is-disabled
          option.removeAttribute('disabled');
          listItems[index].classList.remove('is-disabled');
        }
      }
    }
  }

  _clearFiltersButton() {
    // Check if filter is set
    const listOfFilters = Object.entries(this._refs.queryString)
      .filter((arr) => arr[1])
      .map((arr) => {
        return {
          key: arr[0],
          value: arr[1],
        };
      });

    const removeFilterButton = () => {
      const clearAllButton = this._refs.clearFilter.querySelector('.datatable__clear-filters');
      if (clearAllButton) {
        clearAllButton.remove();
      }
    };

    // Reset filter buttons
    removeFilterButton();
    // Create clear button if filter is added
    if (listOfFilters.length > 0) {
      this._createFilterButton(listOfFilters);
    }
  }

  // Create clear button
  _createFilterButton(listOfFilters) {
    const button = utils.createElement(
      'div',
      {
        class: 'datatable__clear-filters',
        innerText: this._refs.settings.translations.clearFilters,
      },
      this._refs.clearFilter,
    );
    utils.createElement(
      'i',
      {
        class: 'eva eva-close-circle-outline',
      },
      button,
    );

    // Add event listener
    /* eslint-disable-next-line */
    button.addEventListener('click', (e) => {
      for (const obj of listOfFilters) {
        this._refs.queryString[obj.key] = '';
        const filterElem = this._refs.filtersElem.querySelector(`[data-key="${obj.key}"]`);
        if (filterElem) {
          const placeholder = filterElem.querySelector('option');
          const placeholderLabel = placeholder.innerText || placeholder.textContent;
          const placeholderField = filterElem.querySelector('.form-select__placeholder');
          placeholderField.innerText = placeholderLabel;
          placeholder.selected = true;
        }
      }
      this._updateViewItems();
    });
  }

  // Check if string should convert to icon
  _fetchIcon(item) {
    const isIcon = /^\[.*\]$/.test(item);
    const iconName = item.replace(/^\[|]$/gm, ''); // Remove [] from icon name
    const icon = iconName.split('#')[0]; // Icon class name
    const name = isIcon ? icon.split('-').join(' ') : item;
    const color = iconName.split('#')[1] || ''; // Icon color
    return { isIcon, name, color, icon };
  }

  // Check if string should convert to link
  _fetchLink(string) {
    const regex = /(@email-*)|(@phone-*)|(@web-*)/gim;
    const findLink = string.match(regex);
    let isLink = findLink ? true : false;
    let type = findLink ? findLink[0].replace(/[@-]/gm, '') : '';
    let url = findLink ? string.replace(findLink[0], '') : '';
    return { isLink, type, url };
  }

  _fetchFilterValue(val) {
    // return icon name without the []
    const contentObj = this._fetchIcon(val);
    const linkObj = this._fetchLink(val);
    // If link return url
    if (linkObj.isLink) return linkObj.url;
    return contentObj.name;
  }

  /**
   * Sorting
   */
  _initSorting() {
    const elem = this._refs.el.querySelector('.is-asc, .is-desc');
    const sortRow = elem.getAttribute('value');
    const sorOrder = elem.className
      .split(' ')
      .filter((name) => name === 'is-asc' || name === 'is-desc')[0]
      .replace('is-', '');
    // Init sorting
    this._sort(sorOrder, sortRow);
  }

  _sortContentEvent() {
    // Sort event listener
    const listOfSortable = this._refs.table.querySelectorAll('.is-sortable');
    listOfSortable.forEach((item) => {
      item.addEventListener('click', (e) => {
        this._toggleSorting(e.target, listOfSortable);
      });
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.keycode === 13) {
          e.target.click();
        }
      });
    });
  }

  _toggleSorting(elem, list) {
    // Add sort to element
    RestApiDatatable.toggleSorting(elem, list);
    // Update content
    this._updateViewItems();
  }

  _sort(sortOrder, sortValue) {
    this._refs.state.data.sort((a, b) => {
      const sortItemA = sortOrder === 'desc' ? a[sortValue] : b[sortValue];
      const sortItemB = sortOrder === 'desc' ? b[sortValue] : a[sortValue];
      if (typeof a[sortValue] === 'number') return sortItemA - sortItemB;
      if (sortItemB.toUpperCase() < sortItemA.toUpperCase()) {
        return -1;
      }
      if (sortItemB.toUpperCase() > sortItemA.toUpperCase()) {
        return 1;
      }
      return 0;
    });
  }

  /**
   * Event Listeners
   */
  async _updateViewItems() {
    // Update footer
    this._refs.el.setAttribute('data-currentPage', this._refs.currentPage);
    // Reset elements
    this._refs.table.querySelector('.datatable__body').remove();
    // Create Datatable body
    await this._createTableBody();
    // Add / remove clear filter button
    this._clearFiltersButton();
    // Update footer (updateTableFooter)
    event.emit(this._refs.el, 'updateTableFooter');
  }

  _onPagesizeChange(e) {
    this._refs.currentPage = 1;
    this._refs.settings.pageSize.current = Number(e.target.options[e.target.selectedIndex].value);
    this._updateViewItems();
  }

  _onPaginationItemClick(e) {
    this._refs.currentPage = parseInt(e.target.innerHTML);
    this._updateViewItems();
  }

  _onPaginationArrowClick(e) {
    // Direction
    if (e.target.classList.contains('is-left')) {
      this._refs.currentPage--;
    } else {
      this._refs.currentPage++;
    }
    this._updateViewItems();
  }
}
